import { useState, useEffect, useMemo } from "react";
import { collectionGroup, onSnapshot, query, where, orderBy, limit } from "firebase/firestore";
import { dbAdmin } from "../firebaseConfig";
import "../styles/Body.css";
import { getHariLong, getTarikh, capitalizeWords } from "../utils/formatHelpers";
import useUstazMap from "../hooks/useUstazMap";
import {
    flattenPublicHomeEvents,
    getCachedPublicHome,
    loadPublicHome,
    resolvePublicHomeMasjid,
} from "../services/publicMasjidCatalog";

const getStartMs = (k) => {
    if (!k) return 0;
    const value = k.start || k.startsAt || k.date;
    if (value?.seconds) return value.seconds * 1000;
    if (typeof value?.toDate === "function") return value.toDate().getTime();
    if (value instanceof Date) return value.getTime();
    if (Number.isFinite(value)) return value > 1e12 ? value : value * 1000;
    const parsed = new Date(value).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
};

const pad2 = (n) => String(n).padStart(2, "0");
const toYMD = (d) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const parseYMDLocal = (ymd) => {
    const [y, m, d] = String(ymd || "").split("-").map(Number);
    if (!y) return null;
    return new Date(y, (m || 1) - 1, d || 1);
};



const nthDowOfMonth = (year, monthIndex, dow, nth) => {
    // nth: 1..5, or -1 (last)
    if (nth === -1) {
        // last dow
        const lastDay = new Date(year, monthIndex + 1, 0);
        const diff = (lastDay.getDay() - dow + 7) % 7;
        return new Date(year, monthIndex, lastDay.getDate() - diff);
    }
    // first occurrence
    const first = new Date(year, monthIndex, 1);
    const diff = (dow - first.getDay() + 7) % 7;
    const day = 1 + diff + (nth - 1) * 7;
    const candidate = new Date(year, monthIndex, day);
    return candidate.getMonth() === monthIndex ? candidate : null;
};

const expandRecurringRules = (rules = [], masjidId, nowMs, windowDays = 90) => {
    const out = [];
    const now = new Date(nowMs);
    const windowEnd = new Date(nowMs + windowDays * 86400000);

    for (const r of rules) {
        const repeat = r.repeat;
        const startYMD = r.repeatStart;
        const endYMD = r.repeatEnd;
        const time = r.time || "00:00";

        const startDate = parseYMDLocal(startYMD);
        const endDate = parseYMDLocal(endYMD);
        if (!startDate || !endDate) continue;
        endDate.setHours(23, 59, 59, 999);

        // ✅ fallback utk data lama / data yang tak simpan repeatDow/repeatNth
        const base = parseYMDLocal(startYMD);
        const weekOfMonth = (d) => Math.floor((d.getDate() - 1) / 7) + 1;
        const isLastSameDowInMonth = (d) => {
            const x = new Date(d);
            x.setDate(x.getDate() + 7);
            return x.getMonth() !== d.getMonth();
        };

        const derivedDow = base ? base.getDay() : 0;
        const derivedNth = base ? (isLastSameDowInMonth(base) ? -1 : weekOfMonth(base)) : 1;

        // ✅ normalize
        const repeatDow = (r.repeatDow !== undefined && r.repeatDow !== null) ? Number(r.repeatDow) : derivedDow;
        const repeatNth = (r.repeatNth !== undefined && r.repeatNth !== null) ? Number(r.repeatNth) : derivedNth;



        // clamp by window
        const from = new Date(Math.max(startDate.getTime(), now.getTime()));
        const to = new Date(Math.min(endDate.getTime(), windowEnd.getTime()));
        if (from > to) continue;

        if (repeat === "weekly") {
            const dow = repeatDow;
            if (Number.isNaN(dow)) continue;


            // start from "from", find next matching dow
            const cur = new Date(from);
            cur.setHours(0, 0, 0, 0);
            const delta = (dow - cur.getDay() + 7) % 7;
            cur.setDate(cur.getDate() + delta);

            while (cur <= to) {
                const dt = new Date(cur);
                const [hh, mm] = String(time).split(":").map(Number);
                dt.setHours(hh || 0, mm || 0, 0, 0);

                if (dt.getTime() > nowMs) {
                    out.push({
                        ...r,
                        masjidId,
                        eventId: `${r.seriesId || r.seriesKey}-${toYMD(dt)}-${time}`,
                        start: dt,
                    });
                }

                cur.setDate(cur.getDate() + 7);
            }
        }

        if (repeat === "monthly") {
            const dow = repeatDow;
            const nth = repeatNth;
            if (Number.isNaN(dow) || Number.isNaN(nth)) continue;


            // iterate months within window
            let y = from.getFullYear();
            let m = from.getMonth();

            const endY = to.getFullYear();
            const endM = to.getMonth();

            while (y < endY || (y === endY && m <= endM)) {
                const day = nthDowOfMonth(y, m, dow, nth);
                if (day) {
                    const dt = new Date(day);
                    const [hh, mm] = String(time).split(":").map(Number);
                    dt.setHours(hh || 0, mm || 0, 0, 0);

                    if (dt >= from && dt <= to && dt.getTime() > nowMs) {
                        out.push({
                            ...r,
                            masjidId,
                            eventId: `${r.seriesId || r.seriesKey}-${toYMD(dt)}-${time}`,
                            start: dt,
                        });
                    }
                }
                m += 1;
                if (m > 11) { m = 0; y += 1; }
            }
        }
    }

    return out;
};

const normalizeKuliah = (kuliah, masjidId) => {
    const ms = getStartMs(kuliah) || (kuliah.start instanceof Date ? kuliah.start.getTime() : 0);
    const date = ms ? new Date(ms) : null;

    return {
        ...kuliah,
        masjidId,
        date,
        eventId: kuliah.eventId || kuliah.id || "",
        programText: kuliah.program || kuliah.title || "Kuliah",
        ustazText:
            kuliah.speaker ||
            kuliah.nama_ustaz ||
            kuliah.speakerName ||
            kuliah.ustazName ||
            "",
        subjectText: kuliah.subjek || kuliah.kitabCategory || "Umum",
        titleText: kuliah.tajuk || kuliah.kitabTitle || kuliah.title || "",
    };
};

const homeRows = (items) => {
    return flattenPublicHomeEvents(items)
        .map((item) => normalizeKuliah(item, item.masjidId));
};





const Body = ({ v2Masjids = [] }) => {
    const [legacyKuliah, setLegacyKuliah] = useState([]);          // dari aktiviti.kuliah + aktiviti.kuliahRecurring dalam doc masajid
    const [subKuliah, setSubKuliah] = useState([]);                // dari collectionGroup("kuliah")
    const [subRecurringExpanded, setSubRecurringExpanded] = useState([]); // dari collectionGroup("kuliahRecurring") → expanded jadi event list
    const [usingPublicHome, setUsingPublicHome] = useState(null);

    // Satu dokumen suapan awam menggantikan bacaan semua dokumen masjid.
    // Jika suapan tiada, kekalkan keadaan kosong tanpa membaca koleksi lama.
    useEffect(() => {
        let active = true;
        const cached = getCachedPublicHome();
        if (cached) {
            setLegacyKuliah(homeRows(cached));
            setUsingPublicHome(true);
        }

        loadPublicHome()
            .then((items) => {
                if (!active) return;
                if (items) {
                    setLegacyKuliah(homeRows(items));
                    setUsingPublicHome(true);
                    return;
                }
                if (cached) return;
                setLegacyKuliah([]);
                setUsingPublicHome(true);
            })
            .catch((err) => console.error("❌ load public home error:", err));

        return () => { active = false; };
    }, []);

    useEffect(() => {
        if (usingPublicHome !== false) return undefined;
        const now = new Date();
        const q = query(
            collectionGroup(dbAdmin, "kuliah"),
            where("start", ">", now),
            orderBy("start", "asc"),
            limit(100)
        );

        return onSnapshot(
            q,
            (snap) => {
                const rows = snap.docs.map((d) => {
                    const data = d.data();
                    const masjidId = d.ref.parent.parent?.id || "";

                    const ms = getStartMs(data);
                    const date = ms ? new Date(ms) : null;

                    return {
                        ...data,
                        eventId: data.eventId || d.id,
                        masjidId,
                        date,
                        programText: data.program || data.title || "Kuliah",
                        ustazText:
                            data.speaker ||
                            data.nama_ustaz ||
                            data.speakerName ||
                            data.ustazName ||
                            "",
                        subjectText:
                            data.subjek ||
                            data.kitabCategory ||
                            "Umum",
                        titleText:
                            data.tajuk ||
                            data.kitabTitle ||
                            data.title ||
                            "",
                    };
                });

                setSubKuliah(rows);
            },
            (err) => console.error("❌ collectionGroup kuliah error:", err)
        );
    }, [usingPublicHome]);


    useEffect(() => {
        if (usingPublicHome !== false) return undefined;
        const today = toYMD(new Date());
        const q = query(
            collectionGroup(dbAdmin, "kuliahRecurring"),
            where("repeatEnd", ">=", today),
            orderBy("repeatEnd", "asc"),
            limit(200)
        );

        return onSnapshot(
            q,
            (snap) => {
                const now = Date.now();
                const rules = snap.docs.map((d) => {
                    const data = d.data() || {};
                    const masjidId = d.ref.parent.parent?.id || "";

                    // ✅ penting: kalau docId digunakan sebagai seriesId (common case)
                    const seriesId = data.seriesId || d.id;

                    return { ...data, masjidId, seriesId };
                });

                const expanded = rules.flatMap((r) => expandRecurringRules([r], r.masjidId, now, 90));

                const normalized = expanded.map((k) => {
                    const ms = getStartMs(k) || (k.start instanceof Date ? k.start.getTime() : 0);
                    const date = ms ? new Date(ms) : null;

                    return {
                        ...k,
                        date,
                        programText: k.program || k.title || "Kuliah",
                        ustazText:
                            k.speaker ||
                            k.nama_ustaz ||
                            k.speakerName ||
                            k.ustazName ||
                            "",
                        subjectText:
                            k.subjek ||
                            k.kitabCategory ||
                            "Umum",
                        titleText:
                            k.tajuk ||
                            k.kitabTitle ||
                            k.title ||
                            "",
                    };
                });

                setSubRecurringExpanded(normalized);

            },
            (err) => console.error("❌ collectionGroup kuliahRecurring error:", err)
        );

    }, [usingPublicHome]);




    // Sort + limit (ikut kau)
    const mergedKuliah = useMemo(() => {
        const map = new Map();

        const put = (x) => {
            const startMs = getStartMs(x) || (x.start instanceof Date ? x.start.getTime() : 0);
            if (!startMs) return;

            const key =
                x.eventId ? `${x.masjidId}|${x.eventId}` :
                    `${x.masjidId}|${startMs}|${x.title || ""}|${x.speaker || ""}`;

            if (!map.has(key)) {
                map.set(key, { ...x, date: new Date(startMs) });
            }
        };

        [...legacyKuliah, ...subKuliah, ...subRecurringExpanded].forEach(put);

        return Array.from(map.values())
            .filter((k) => k.date instanceof Date && !isNaN(k.date.getTime()))
            .filter((k) => k.date.getTime() > Date.now())
            .sort((a, b) => a.date - b.date)
            .slice(0, 12);
    }, [legacyKuliah, subKuliah, subRecurringExpanded]);


    const speakerIds = useMemo(() => {
        // unique speakerId sahaja
        const set = new Set(
            mergedKuliah
                .map(k => k.speakerId)
                .filter(Boolean)
        );
        return Array.from(set);
    }, [mergedKuliah]);

    const ustazMap = useUstazMap(speakerIds);




    return (
        <div className="container-body">
            {/* Kuliah Section */}
            <div className="column-body kuliah">

                <div className="title-area">KULIAH TERKINI</div>

                <div className="cards-container">
                    {mergedKuliah.map((kuliah, index) => {

                        const live = kuliah.speakerId ? ustazMap[kuliah.speakerId] : null;

                        const liveImg =
                            live?.gambar || live?.image || live?.photoURL ||
                            kuliah.speakerImage || kuliah.gambar_ustaz || "";

                        const masjid = resolvePublicHomeMasjid(v2Masjids, kuliah);
                        const masjidName = capitalizeWords(masjid?.profail?.nama || kuliah.masjidId);

                        // logo fallback: logo -> logoJa -> gambar
                        const masjidLogo =
                            masjid?.profail?.logo ||
                            masjid?.profail?.logoJa ||
                            masjid?.profail?.gambar;

                        // banner
                        const bannerImg = masjid?.profail?.gambar || masjidLogo;

                        return (
                            <div className="card" key={`${kuliah.eventId || index}`}>
                                <div className="card-image">
                                    {bannerImg ? <img src={bannerImg} alt="Masjid" /> : <div className="card-image-fallback" />}
                                </div>

                                <div className="card-content">
                                    {/* ✅ ikut susunan UI kau: mosque-row di atas */}
                                    <div className="mosque-row">
                                        {masjidLogo && (
                                            <img
                                                className="mosque-logo"
                                                src={masjidLogo}
                                                alt="Logo masjid"
                                                onError={(e) => (e.currentTarget.style.display = "none")}
                                            />
                                        )}
                                        <p className="mosque-name-body">{masjidName}</p>
                                    </div>

                                    <div className="card-header">
                                        <h3>{kuliah.programText}</h3>

                                        <p className="date">
                                            {kuliah.date ? `${getHariLong(kuliah.date)}, ${getTarikh(kuliah.date)}` : ""}
                                        </p>

                                        <p className="ustaz-name">{kuliah.ustazText}</p>
                                    </div>

                                    {/* ✅ Footer: kitab cover + subject + title (kekal macam kau) */}
                                    <div className={`card-footer ${kuliah.kitabCoverImage ? "has-kitab" : ""}`}>
                                        {kuliah.kitabCoverImage && (
                                            <img
                                                className="kitab-cover"
                                                src={kuliah.kitabCoverImage}
                                                alt={kuliah.kitabTitle || "Kitab"}
                                                onError={(e) => (e.currentTarget.style.display = "none")}
                                            />
                                        )}

                                        <div className="footer-right">
                                            <div className="subject">
                                                <span className="subject-label">{kuliah.subjectText}</span>
                                            </div>

                                            <p className="title">{kuliah.titleText}</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Avatar ustaz */}
                                <div className="avatar-wrapper">
                                    <div className="avatar">
                                        <img src={liveImg} alt="Ustaz" />
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>


            </div>

        </div>
    );
};

export default Body;
